// frontend/components/FormTPreview.jsx
import React, { useState ,useRef} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Divider,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Edit as EditIcon,
} from '@mui/icons-material';

const FormTPreview = ({ open, onClose, formData, onEdit, onDownload, readOnly = false }) => {
  const [zoom, setZoom] = useState(100);
  const previewContentRef = useRef(null);

  if (!formData) return null;

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Form T - ${formData.application_no}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: 'Times New Roman', Times, serif;
              margin: 0;
              padding: 20px;
              background: white;
            }
            .print-container {
              max-width: 210mm;
              margin: 0 auto;
              background: white;
              padding: 40px;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
            }
            .title {
              font-size: 20px;
              font-weight: bold;
              text-align: center;
              margin: 10px 0;
            }
            .subtitle {
              text-align: center;
              margin: 5px 0;
              font-size: 14px;
            }
            .application-no {
              text-align: right;
              margin: 20px 0;
              font-size: 14px;
            }
            .section {
              margin: 20px 0;
            }
            .party {
              margin: 15px 0;
            }
            .party-name {
              font-weight: bold;
              margin-bottom: 5px;
            }
            .content {
              margin: 20px 0;
              text-align: justify;
              line-height: 1.6;
            }
            .signature {
              margin-top: 50px;
            }
            .footer {
              margin-top: 30px;
              font-size: 12px;
              text-align: center;
              border-top: 1px solid #ccc;
              padding-top: 20px;
            }
            hr {
              margin: 20px 0;
              border: none;
              border-top: 1px solid #000;
            }
            @media print {
              body {
                padding: 0;
                margin: 0;
              }
              .print-container {
                padding: 20px;
                max-width: 100%;
              }
              .no-print {
                display: none;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            <div class="header">
              <div class="title">FORM T</div>
              <div class="subtitle">[See Rule 19]</div>
              <div class="title" style="margin-top: 20px;">Application for recovery of gratuity</div>
              <div class="subtitle">Before the Controlling Authority under the Payment of Gratuity Act, 1972</div>
            </div>

            <div class="application-no">
              <strong>Application No:</strong> ${formData.application_no}<br>
              <strong>Date:</strong> ${new Date(formData.application_date).toLocaleDateString('en-IN')}
            </div>

            <div class="section">
              <div class="party">
                <div class="party-name">Between:</div>
                <div><strong>${formData.applicant_name}</strong></div>
                <div>${formData.applicant_address}</div>
                <div><em>${formData.applicant_type === 'employee' ? 'Applicant' : 
                  formData.applicant_type === 'nominee' ? 'Nominee of late employee' : 
                  'Legal heir of late employee'}</em></div>
              </div>
              
              <div class="party">
                <div class="party-name">And</div>
                <div><strong>${formData.employer_name}</strong></div>
                <div>${formData.employer_address}</div>
                <div><em>Employer</em></div>
              </div>
            </div>

            <hr>

            <div class="content">
              <p>
                The applicant is ${formData.applicant_type === 'employee' ? 'an employee' : 
                  formData.applicant_type === 'nominee' ? `a nominee of late ${formData.employee_name}` : 
                  `a legal heir of late ${formData.employee_name}`} 
                of the above-mentioned employer and you were pleased to direct the said employer in your notice 
                dated ${new Date(formData.notice_date).toLocaleDateString('en-IN')} under rule 10 of the Payment of 
                Gratuity (Central) Rules, 1972 for payment of a sum of <strong>Rs. ${formData.gratuity_amount?.toLocaleString('en-IN')}</strong> 
                as gratuity payable under the Payment of Gratuity Act, 1972.
              </p>
              
              <p>
                <strong>2.</strong> The applicant submits that the said employer failed to pay the said amount 
                of gratuity to me as directed by you although I approached him for payment.
              </p>
              
              <p>
                <strong>3.</strong> The applicant therefore prays that a certificate may be issued under section 8 
                of the said Act for the recovery of the said sum of <strong>Rs. ${formData.gratuity_amount?.toLocaleString('en-IN')}</strong> 
                due to me as gratuity in terms of your direction.
              </p>
            </div>

            <div class="signature">
              <div><strong>Place:</strong> ${formData.place || formData.applicant_address?.split(',').pop()?.trim()}</div>
              <div><strong>Date:</strong> ${new Date(formData.application_date).toLocaleDateString('en-IN')}</div>
              <br><br>
              <div><strong>Signature of Applicant:</strong></div>
              <div style="margin-top: 10px; border-top: 1px solid #000; width: 300px;">${formData.applicant_name}</div>
            </div>


          </div>
          <script>
            window.onload = () => {
              window.print();
              window.onafterprint = () => window.close();
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'APPROVED': return '#4caf50';
      case 'REJECTED': return '#f44336';
      case 'PENDING': return '#ff9800';
      default: return '#9e9e9e';
    }
  };

  const getApplicantDescription = () => {
    if (formData.applicant_type === 'employee') {
      return 'an employee';
    } else if (formData.applicant_type === 'nominee') {
      return `a nominee of late ${formData.employee_name}`;
    } else {
      return `a legal heir of late ${formData.employee_name}`;
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(150, prev + 10));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(50, prev - 10));
  };

  const handleZoomReset = () => {
    setZoom(100);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          height: '85vh',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #e0e0e0',
        bgcolor: 'primary.main',
        color: 'white',
        flexShrink: 0
      }}>
        <Box>
          <Typography variant="h6">
            Form T Preview
          </Typography>
          <Typography variant="caption">
            {formData.application_no}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Toolbar */}
      <Box sx={{ 
        p: 2, 
        borderBottom: '1px solid #e0e0e0',
        bgcolor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1,
        flexShrink: 0
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Zoom:
          </Typography>
          <IconButton size="small" onClick={handleZoomOut} disabled={zoom <= 50}>
            <ZoomOutIcon fontSize="small" />
          </IconButton>
          <Typography variant="body2" sx={{ minWidth: '45px', textAlign: 'center' }}>
            {zoom}%
          </Typography>
          <IconButton size="small" onClick={handleZoomIn} disabled={zoom >= 150}>
            <ZoomInIcon fontSize="small" />
          </IconButton>
          <Button size="small" variant="outlined" onClick={handleZoomReset}>
            Reset
          </Button>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip 
            label={formData.status}
            sx={{ 
              bgcolor: getStatusColor(formData.status),
              color: 'white',
              fontWeight: 'bold',
            }}
            size="small"
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {formData.status === 'APPROVED' && (
            <>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={onDownload}
              >
                Download PDF
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
              >
                Print
              </Button>
            </>
          )}
          
          {!readOnly && formData.status === 'REJECTED' && (
            <Button
              variant="contained"
              color="warning"
              size="small"
              startIcon={<EditIcon />}
              onClick={onEdit}
            >
              Edit & Resubmit
            </Button>
          )}
        </Box>
      </Box>

      {/* Scrollable Preview Content - Fixed height with overflow */}
      <DialogContent sx={{ 
        p: 3, 
        flex: 1,
        overflow: 'auto',
        bgcolor: '#e0e0e0',
        display: 'flex',
        justifyContent: 'center'
      }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            minHeight: '100%',
          }}
        >
          <Paper 
            elevation={3}
            sx={{
              width: '210mm', // Fixed A4 width
              maxWidth: '100%',
              minHeight: '297mm', // Fixed A4 height minimum
              bgcolor: 'white',
              position: 'relative',
              overflow: 'visible',
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease',
              '& .watermark': {
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%) rotate(-45deg)',
                fontSize: '80px',
                opacity: 0.1,
                pointerEvents: 'none',
                whiteSpace: 'nowrap',
                zIndex: 1
              }
            }}
          >
  
            
            {/* Preview Content - Everything inside this box stays within paper */}
            <Box id="form-t-preview-content" sx={{ p: 4 }}>
              {/* Header */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h5" fontWeight="bold">
                  FORM T
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  [See Rule 19]
                </Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ mt: 2 }}>
                  Application for recovery of gratuity
                </Typography>
                <Typography variant="body2">
                  Before the Controlling Authority under the Payment of Gratuity Act, 1972
                </Typography>
              </Box>

              {/* Application Details */}
              <Box sx={{ textAlign: 'right', mb: 3 }}>
                <Typography variant="body2">
                  <strong>Application No:</strong> {formData.application_no}
                </Typography>
                <Typography variant="body2">
                  <strong>Date:</strong> {new Date(formData.application_date).toLocaleDateString('en-IN')}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Parties */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Between:
                </Typography>
                <Box sx={{ ml: 2, mt: 1, mb: 2 }}>
                  <Typography variant="body1" fontWeight="medium">
                    {formData.applicant_name}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                    {formData.applicant_address}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({formData.applicant_type === 'employee' ? 'Applicant' : 
                      formData.applicant_type === 'nominee' ? 'Nominee of late employee' : 
                      'Legal heir of late employee'})
                  </Typography>
                </Box>

                <Typography variant="subtitle1" fontWeight="bold">
                  And
                </Typography>
                <Box sx={{ ml: 2, mt: 1 }}>
                  <Typography variant="body1" fontWeight="medium">
                    {formData.employer_name}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                    {formData.employer_address}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    (Employer)
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Content */}
              <Box sx={{ textAlign: 'justify', mb: 3, lineHeight: 1.8 }}>
                <Typography variant="body2" paragraph>
                  The applicant is {getApplicantDescription()} and you were pleased 
                  to direct the said employer in your notice dated {new Date(formData.notice_date).toLocaleDateString('en-IN')} 
                  under rule 10 of the Payment of Gratuity (Central) Rules, 1972 for payment 
                  of a sum of <strong>Rs. {formData.gratuity_amount?.toLocaleString('en-IN')}</strong> 
                  as gratuity payable under the Payment of Gratuity Act, 1972.
                </Typography>

                <Typography variant="body2" paragraph>
                  <strong>2.</strong> The applicant submits that the said employer failed 
                  to pay the said amount of gratuity to me as directed by you although I 
                  approached him for payment.
                </Typography>

                <Typography variant="body2" paragraph>
                  <strong>3.</strong> The applicant therefore prays that a certificate 
                  may be issued under section 8 of the said Act for the recovery of the 
                  said sum of <strong>Rs. {formData.gratuity_amount?.toLocaleString('en-IN')}</strong> 
                  due to me as gratuity in terms of your direction.
                </Typography>
              </Box>

              {/* Signature */}
              <Box sx={{ mt: 6 }}>
                <Typography variant="body2">
                  <strong>Place:</strong> {formData.place || formData.applicant_address?.split(',').pop()?.trim()}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Date:</strong> {new Date(formData.application_date).toLocaleDateString('en-IN')}
                </Typography>
                <Box sx={{ mt: 4 }}>
                  <Typography variant="body2">
                    <strong>Signature of Applicant:</strong>
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 2, fontFamily: 'cursive', borderTop: '1px solid #ccc', display: 'inline-block', pt: 1 }}>
                    {formData.applicant_name}
                  </Typography>
                </Box>
              </Box>

            
            </Box>
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2, flexShrink: 0 }}>
        <Button onClick={onClose}>Close</Button>
        {formData.status === 'APPROVED' && (
          <Button 
            variant="contained" 
            color="primary" 
            onClick={onDownload}
            startIcon={<DownloadIcon />}
          >
            Download PDF
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FormTPreview;